import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import toast from "react-hot-toast";
import JobListingService from "../../../services/KangarooServices/JobListingService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";

const AddJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [initialData, setInitialData] = useState({});

  const fetchDetails = async () => {
    try {
      const result = await JobListingService.getDetail(id);
      setInitialData(result);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch job listing details");
    }
  };

  useEffect(() => {
    if (id) {
      fetchDetails();
    }
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      position: initialData.position || "",
      status: initialData.status !== undefined ? initialData.status : 1,
      slotsLeft: initialData.slotsLeft || 0,
      publishedDate: initialData?.publishedDate || "",
      deadLine: initialData.deadLine || "",
      jobDescription: initialData.jobDescription || "",
      jobSpecifications: initialData.jobSpecifications || "",
      experience: initialData.experience || "",
      education: initialData.education || "",
    },
    validationSchema: Yup.object({
      position: Yup.string()
        .required("Position is required")
        .min(3, "Position must be at least 3 characters")
        .max(100, "Position must be at most 100 characters"),
      status: Yup.number()
        .required("Status is required")
        .oneOf([1, 2], "Status must be either 1 or 2"),
      slotsLeft: Yup.number()
        .required("Slots left is required")
        .min(0, "Slots left cannot be negative"),
      publishedDate: Yup.date()
        .required("Published date is required")
        .typeError("Please select a valid date"),
      deadLine: Yup.date()
        .required("Deadline is required")
        .typeError("Please select a valid date")
        .min(Yup.ref("publishedDate"), "Deadline must be after published date"),
      jobDescription: Yup.string()
        .required("Job description is required")
        .min(10, "Job description must be at least 10 characters"),
      jobSpecifications: Yup.string()
        .required("Job specifications are required")
        .min(10, "Job specifications must be at least 10 characters"),
      experience: Yup.string()
        .required("Experience is required")
        .min(3, "Experience must be at least 3 characters"),
      education: Yup.string()
        .required("Education is required")
        .min(3, "Education must be at least 3 characters"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          position: values.position,
          status: parseInt(values.status, 10), // Fixed: use base 10
          slotsLeft: parseInt(values.slotsLeft, 10),

          publishedDate: values.publishedDate,

          deadLine: values.deadLine,
          jobDescription: values.jobDescription,
          jobSpecifications: values.jobSpecifications,
          experience: values.experience,
          education: values.education,
        };

        console.log("Payload being sent:", payload);

        if (id) {
          await JobListingService.update(id, payload);
          toast.success("Job listing updated successfully");
        } else {
          await JobListingService.create(payload);
          toast.success("Job listing created successfully");
        }
        navigate("/joblisting");
      } catch (error) {
        console.error("Error:", error);
        console.error("Error response:", error.response?.data);
        toast.error(
          error.response?.data?.message ||
            "An error occurred. Please try again",
        );
      }
    },
  });

  const breadcrumbItems = [{ title: "Back to List", link: "/joblisting" }];

  return (
    <Container fluid className="">
      <div className="page-content">
        <BreadCrumb
          title={id ? "Edit Job Listing" : "Add Job Listing"}
          pageTitle="Job Listings"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex justify-content-end gap-2 mb-2">
            <Button type="submit" color="success">
              {id ? "Update" : "Create"}
            </Button>
            <Button
              type="button"
              color="danger"
              onClick={() => navigate("/joblisting")}
            >
              Cancel
            </Button>
          </div>

          <Row className="g-4">
            {/* Basic Information Card */}
            <Col lg="8">
              <Card className="border-0 shadow-sm mb-4">
                <CardHeader className="bg-light border-0">
                  <h5 className="card-title mb-0 fw-bold">Basic Information</h5>
                </CardHeader>
                <CardBody className="p-4">
                  {/* Position */}
                  <FormGroup className="mb-4">
                    <Label className="fw-semibold mb-2">
                      Position Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="position"
                      placeholder="e.g. Senior Software Engineer"
                      value={formik.values.position}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="form-control-lg"
                      invalid={
                        formik.touched.position && !!formik.errors.position
                      }
                    />
                    {formik.touched.position && formik.errors.position && (
                      <FormFeedback>{formik.errors.position}</FormFeedback>
                    )}
                  </FormGroup>

                  <Row className="g-3">
                    <Col md="4">
                      {/* Status */}
                      <FormGroup>
                        <Label className="fw-semibold mb-2">
                          Status <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="select"
                          name="status"
                          value={formik.values.status}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="form-select-lg"
                          invalid={
                            formik.touched.status && !!formik.errors.status
                          }
                        >
                          <option value={1}>Receiving Applications</option>
                          <option value={2}>Closed</option>
                        </Input>
                        {formik.touched.status && formik.errors.status && (
                          <FormFeedback>{formik.errors.status}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md="4">
                      {/* Slots Left */}
                      <FormGroup>
                        <Label className="fw-semibold mb-2">
                          Slots Left <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="number"
                          name="slotsLeft"
                          placeholder="e.g. 3"
                          value={formik.values.slotsLeft}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="form-control-lg"
                          invalid={
                            formik.touched.slotsLeft &&
                            !!formik.errors.slotsLeft
                          }
                        />
                        {formik.touched.slotsLeft &&
                          formik.errors.slotsLeft && (
                            <FormFeedback>
                              {formik.errors.slotsLeft}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>

                    <Col md="4">
                      {/* Experience */}
                      <FormGroup>
                        <Label className="fw-semibold mb-2">
                          Experience <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="experience"
                          placeholder="e.g. 3-5 years"
                          value={formik.values.experience}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="form-control-lg"
                          invalid={
                            formik.touched.experience &&
                            !!formik.errors.experience
                          }
                        />
                        {formik.touched.experience &&
                          formik.errors.experience && (
                            <FormFeedback>
                              {formik.errors.experience}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Requirements Card */}
              <Card className="border-0 shadow-sm mb-4">
                <CardHeader className="bg-light border-0">
                  <h5 className="card-title mb-0 fw-bold">
                    Educational Requirements
                  </h5>
                </CardHeader>
                <CardBody className="p-4">
                  {/* Education */}
                  <FormGroup>
                    <Label className="fw-semibold mb-2">
                      Education <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="education"
                      placeholder="e.g. Bachelor's degree in Computer Science"
                      value={formik.values.education}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="form-control-lg"
                      invalid={
                        formik.touched.education && !!formik.errors.education
                      }
                    />
                    {formik.touched.education && formik.errors.education && (
                      <FormFeedback>{formik.errors.education}</FormFeedback>
                    )}
                  </FormGroup>
                </CardBody>
              </Card>

              {/* Job Description Card */}
              <Card className="border-0 shadow-sm mb-4">
                <CardHeader className="bg-light border-0">
                  <h5 className="card-title mb-0 fw-bold">Job Description</h5>
                </CardHeader>
                <CardBody className="p-4">
                  <FormGroup>
                    <div className="border rounded">
                      <CKEditor
                        editor={ClassicEditor}
                        data={formik.values.jobDescription}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          formik.setFieldValue("jobDescription", data);
                        }}
                        onBlur={() =>
                          formik.setFieldTouched("jobDescription", true)
                        }
                      />
                    </div>
                    {formik.touched.jobDescription &&
                      formik.errors.jobDescription && (
                        <div className="text-danger mt-2 small">
                          {formik.errors.jobDescription}
                        </div>
                      )}
                  </FormGroup>
                </CardBody>
              </Card>

              {/* Job Specifications Card */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light border-0">
                  <h5 className="card-title mb-0 fw-bold">
                    Job Specifications
                  </h5>
                </CardHeader>
                <CardBody className="p-4">
                  <FormGroup>
                    <div className="border rounded">
                      <CKEditor
                        editor={ClassicEditor}
                        data={formik.values.jobSpecifications}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          formik.setFieldValue("jobSpecifications", data);
                        }}
                        onBlur={() =>
                          formik.setFieldTouched("jobSpecifications", true)
                        }
                      />
                    </div>
                    {formik.touched.jobSpecifications &&
                      formik.errors.jobSpecifications && (
                        <div className="text-danger mt-2 small">
                          {formik.errors.jobSpecifications}
                        </div>
                      )}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            {/* Sidebar - Timeline Card */}
            <Col lg="4">
              <Card
                className="border-0 shadow-sm position-sticky"
                style={{ top: "100px" }}
              >
                <CardHeader className="bg-light border-0">
                  <h5 className="card-title mb-0 fw-bold">Timeline & Dates</h5>
                </CardHeader>
                <CardBody className="p-4">
                  {/* Published Date */}
                  <FormGroup className="mb-4">
                    <Label className="fw-semibold mb-2">
                      Published Date <span className="text-danger">*</span>
                    </Label>
                    <Flatpickr
                      className="form-control form-control-lg"
                      value={
                        formik.values.publishedDate
                          ? new Date(formik.values.publishedDate)
                          : null
                      }
                      onChange={(date, dateStr) => {
                        formik.setFieldValue("publishedDate", dateStr);
                      }}
                      options={{
                        dateFormat: "Y-m-d",
                        enableTime: false,
                      }}
                      placeholder="Select published date"
                    />
                    {formik.touched.publishedDate &&
                      formik.errors.publishedDate && (
                        <div className="text-danger mt-1 small">
                          {formik.errors.publishedDate}
                        </div>
                      )}
                    <small className="text-muted d-block mt-2">
                      When the job listing goes live
                    </small>
                  </FormGroup>

                  {/* Deadline */}
                  <FormGroup>
                    <Label className="fw-semibold mb-2">
                      Application Deadline{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <Flatpickr
                      className="form-control form-control-lg"
                      value={
                        formik.values.deadLine
                          ? new Date(formik.values.deadLine)
                          : null
                      }
                      onChange={(date, dateStr) => {
                        formik.setFieldValue("deadLine", dateStr);
                      }}
                      options={{
                        dateFormat: "Y-m-d",
                        enableTime: false,
                      }}
                      placeholder="Select deadLine"
                    />
                    {formik.touched.deadLine && formik.errors.deadLine && (
                      <div className="text-danger mt-1 small">
                        {formik.errors.deadLine}
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      Last date to accept applications
                    </small>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>
      </div>
    </Container>
  );
};

export default AddJob;
